// src/app/layout/TopBar.jsx
import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LanguageIcon from "@mui/icons-material/Language";
import NotificationsIcon from "@mui/icons-material/Notifications";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Button,
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
  Stack,
  Typography,
} from "@mui/material";

import { useAuth } from "../providers/AuthProvider";
import { useEmpresa } from "../providers/EmpresaProvider";
import { getStockTopBarBreadcrumbs } from "../../features/stock/constants/stockAreas";

export default function TopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location?.pathname || "";

  const { user } = useAuth();
  const { empresaNombre } = useEmpresa();

  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef(null);

  const title = useMemo(() => {
    if (pathname.startsWith("/consultora/insumos-globales")) return "Configuraciones generales";
    if (pathname.startsWith("/consultora")) return "Inicio";
    if (pathname.startsWith("/configuraciones")) return "Configuraciones";
    if (pathname.startsWith("/tesoreria")) return "Tesorería";
    if (pathname.startsWith("/rrhh")) return "Recursos Humanos";
    if (pathname.startsWith("/stock")) return "Módulo de Stock";
    if (pathname.startsWith("/inicio")) return "Inicio";
    return "Inicio";
  }, [pathname]);

  const stockBreadcrumbs = useMemo(() => {
    if (!pathname.startsWith("/stock")) return null;
    return getStockTopBarBreadcrumbs(pathname);
  }, [pathname]);

  const userRole = String(user?.user_role || "").toUpperCase();
  const isConsultant = userRole === "CONSULTORA";
  const isConsultantArea = pathname.startsWith("/consultora");
  const canChangeLicense = isConsultant && !isConsultantArea;
  const consultantName = user?.display_name || user?.displayName || user?.name || "Consultora";

  const rightLabel = useMemo(() => {
    if (isConsultantArea) return consultantName;
    return empresaNombre?.trim() || "Sin licencia";
  }, [consultantName, empresaNombre, isConsultantArea]);

  const handleToggleMenu = useCallback(() => {
    if (!canChangeLicense) return;
    setMenuOpen((prev) => !prev);
  }, [canChangeLicense]);

  const handleCloseMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const handleGoToChangeLicense = useCallback(() => {
    setMenuOpen(false);
    navigate("/consultora");
  }, [navigate]);

  return (
    <ClickAwayListener onClickAway={handleCloseMenu}>
      <Box
        sx={{
          height: "8vh",
          px: 1.5,
          py: 2,
          borderRadius: "55px",
          boxShadow: "inset 0px 0px 4px rgba(165, 165, 178, 1)",
          backgroundColor: "neutral.150",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" flex={1}>
          <LanguageIcon sx={{ width: 36, height: 36, color: "primary.500" }} />
          {stockBreadcrumbs ? (
            <Stack
              direction="row"
              spacing={0.8}
              alignItems="center"
              sx={{ flex: 1, minWidth: 0 }}
            >
              {stockBreadcrumbs.map((item, index) => (
                <Stack
                  key={item.label}
                  direction="row"
                  spacing={0.8}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  {index > 0 && (
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 500,
                        color: "rgba(15, 23, 42, 0.45)",
                        lineHeight: 1,
                      }}
                    >
                      /
                    </Typography>
                  )}

                  <Typography
                    component={item.to ? "button" : "span"}
                    type={item.to ? "button" : undefined}
                    onClick={item.to ? () => navigate(item.to) : undefined}
                    sx={{
                      p: 0,
                      border: 0,
                      bgcolor: "transparent",
                      font: "inherit",
                      fontSize: 18,
                      fontWeight: index === stockBreadcrumbs.length - 1 ? 600 : 500,
                      color: item.to ? "primary.700" : "primary.900",
                      lineHeight: 1,
                      cursor: item.to ? "pointer" : "default",
                      textAlign: "left",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: { xs: 170, md: 260 },
                      "&:hover": item.to
                        ? {
                            color: "primary.900",
                            textDecoration: "underline",
                            textUnderlineOffset: "4px",
                          }
                        : undefined,
                    }}
                  >
                    {item.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "primary.900", flex: 1 }}>
              {title}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={4} alignItems="center" flex={1} justifyContent="flex-end">
          <NotificationsIcon sx={{ width: 30, height: 30, color: "primary.500" }} />

          <Box sx={{ position: "relative" }}>
            {isConsultantArea ? (
              <Box
                aria-label={`Consultora ${consultantName}`}
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: "primary.500",
                  borderRadius: "33px",
                  boxShadow:
                    "inset 0px 0px 3px rgba(192, 192, 201, 0.5), 0px 5px 14px rgba(16, 47, 148, 0.28)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: "logoblanco", lineHeight: 1 }}>
                  {rightLabel}
                </Typography>
              </Box>
            ) : (
              <Button
                ref={anchorRef}
                variant="contained"
                disableElevation
                onClick={handleToggleMenu}
                sx={{
                  px: 2.5,
                  py: 1,
                  backgroundColor: "primary.500",
                  borderRadius: "33px",
                  boxShadow: "inset 0px 0px 3px rgba(192, 192, 201, 0.5)",
                  "&:hover": { backgroundColor: canChangeLicense ? "primary.600" : "primary.500" },
                  textTransform: "none",
                  cursor: canChangeLicense ? "pointer" : "default",
                  minWidth: 0,
                }}
              >
                <Stack direction="row" spacing={0.7} alignItems="center">
                  <Typography sx={{ fontSize: 16, fontWeight: 600, color: "logoblanco.main", lineHeight: 1 }}>
                    {rightLabel}
                  </Typography>
                  {canChangeLicense ? (
                    <KeyboardArrowDownIcon
                      sx={{
                        color: "logoblanco.main",
                        fontSize: 20,
                        transition: "transform .18s ease",
                        transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  ) : null}
                </Stack>
              </Button>
            )}

            {canChangeLicense ? (
              <Popper
                open={menuOpen}
                anchorEl={anchorRef.current}
                placement="bottom-end"
                transition
                modifiers={[{ name: "offset", options: { offset: [0, 10] } }]}
                sx={{ zIndex: 1400 }}
              >
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps} timeout={180}>
                    <Paper
                      elevation={0}
                      sx={{
                        minWidth: 220,
                        p: 0.8,
                        borderRadius: "16px",
                        bgcolor: "neutral.50",
                        boxShadow: "0 12px 30px rgba(2, 8, 23, 0.16)",
                        border: "1px solid rgba(15, 23, 42, 0.08)",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        onClick={handleGoToChangeLicense}
                        sx={{
                          px: 1.4,
                          py: 1.1,
                          borderRadius: "12px",
                          cursor: "pointer",
                          transition: "background .15s ease",
                          "&:hover": { bgcolor: "secondary.50" },
                        }}
                      >
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "primary.900" }}>
                          Cambiar licencia
                        </Typography>
                      </Box>
                    </Paper>
                  </Fade>
                )}
              </Popper>
            ) : null}
          </Box>
        </Stack>
      </Box>
    </ClickAwayListener>
  );
}
